/* Bookstore login + register — `/bookstore/login`.
 * Email + password (separate from The Loom Google OAuth).
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, Mail, Lock, User, ChevronLeft, Loader2 } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import PasswordStrengthMeter, { scorePassword } from '@/components/PasswordStrengthMeter';

export default function BookstoreLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/bookstore';
  const { refresh } = useBookstoreAuth();
  const [mode, setMode] = useState(params.get('forgot') === '1' ? 'forgot' : 'signin');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [sentTo, setSentTo] = useState('');   // iter55g44 — reset-request confirmation

  useSeoMeta('/bookstore/login', {
    title: 'Sign in · Crown & Rose',
    description: 'Sign in to the Crown & Rose store to manage your bookshelf and Weekly Maven subscription.',
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && scorePassword(form.password).score < 2) {
      toast.error('Please choose a stronger password.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'register') {
        await bookstoreApi.register(form);
        toast.success('Welcome to Crown & Rose.');
        await refresh();
        navigate(next, { replace: true });
      } else if (mode === 'forgot') {
        // iter55g44 — Password recovery. Backend always returns 200
        // (whether or not the email exists) to prevent enumeration.
        await bookstoreApi.requestPasswordReset(form.email);
        setSentTo(form.email);
      } else {
        await bookstoreApi.login({ email: form.email, password: form.password });
        toast.success('Signed in.');
        await refresh();
        navigate(next, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground" data-testid="bs-login-page">
      <div className="w-full max-w-md">
        <Link to="/bookstore" data-testid="bs-login-back" className="mb-4 inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-3 w-3" /> Back to the Book Store
        </Link>
        <div className="rounded-xl border border-border bg-card p-7 shadow-panel-active">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground">Crown & Rose</div>
              <div className="text-[15px] font-semibold">
                {mode === 'register' && 'Create your account'}
                {mode === 'signin'   && 'Welcome back'}
                {mode === 'forgot'   && 'Reset your password'}
              </div>
            </div>
          </div>

          {/* iter55g44 — Reset-request success state. */}
          {mode === 'forgot' && sentTo ? (
            <div className="rounded-md border border-emerald-400/50 bg-emerald-50/60 p-4 text-[13px] text-emerald-900" data-testid="bs-login-reset-sent">
              <p className="font-semibold">Check your inbox</p>
              <p className="mt-1 text-[12px]">
                If an account exists for <span className="font-mono-stat">{sentTo}</span>, a password-reset link is on its way. The link expires in 60 minutes.
              </p>
              <button
                type="button"
                onClick={() => { setMode('signin'); setSentTo(''); }}
                data-testid="bs-login-reset-back"
                className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-900 hover:underline"
              >
                <ChevronLeft className="h-3 w-3" /> Back to sign in
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-3">
            {mode === 'register' && (
              <label className="block">
                <span className="mb-1 block text-[11.5px] text-muted-foreground">Your name</span>
                <div className="relative">
                  <User className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="text" required value={form.name} onChange={set('name')}
                    placeholder="Jane Reader"
                    data-testid="bs-login-name"
                    className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-[13px] outline-none focus:border-primary"
                  />
                </div>
              </label>
            )}
            <label className="block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">Email</span>
              <div className="relative">
                <Mail className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="email" required value={form.email} onChange={set('email')}
                  placeholder="you@yourdomain.com"
                  data-testid="bs-login-email"
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-[13px] outline-none focus:border-primary"
                />
              </div>
            </label>
            {mode !== 'forgot' && (
            <label className="block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">Password</span>
              <div className="relative">
                <Lock className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="password" required value={form.password} onChange={set('password')}
                  minLength={8}
                  placeholder="Minimum 8 characters"
                  data-testid="bs-login-password"
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-[13px] outline-none focus:border-primary"
                />
              </div>
              {mode === 'register' && <PasswordStrengthMeter password={form.password} />}
              {mode === 'signin' && (
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  data-testid="bs-login-forgot"
                  className="mt-1.5 text-[11.5px] text-muted-foreground hover:text-primary hover:underline"
                >
                  Forgot password?
                </button>
              )}
            </label>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="bs-login-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {mode === 'register' && 'Create account'}
              {mode === 'signin'   && 'Sign in'}
              {mode === 'forgot'   && 'Email me a reset link'}
            </button>
          </form>
          )}

          {!(mode === 'forgot' && sentTo) && (
          <button
            type="button"
            onClick={() => setMode(mode === 'register' ? 'signin' : (mode === 'forgot' ? 'signin' : 'register'))}
            data-testid="bs-login-toggle"
            className="mt-4 w-full text-center text-[12px] text-muted-foreground hover:text-foreground"
          >
            {mode === 'register' && 'Already have an account? Sign in'}
            {mode === 'signin'   && 'New here? Create your account →'}
            {mode === 'forgot'   && '← Back to sign in'}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
